import React, { useState } from "react";
import Select, { components as rsComponents } from "react-select";

// react-select doesn't forward arbitrary DOM props (like autoComplete) down
// to the real <input> it renders internally — passing autoComplete="off"
// straight to <Select> is silently dropped, and Chrome/Edge/Firefox all
// ignore autocomplete="off" on a plain text input anyway (a long-standing
// browser "feature": they still show their own saved-value suggestions).
// Overriding the Input sub-component to render a nonsense autoComplete
// value is the standard workaround (browsers only respect off/none-lookalike
// values as a "don't offer suggestions" signal, not the literal word
// "off") — this is what actually stops the browser's own autofill list from
// popping up and covering react-select's OWN options menu underneath it.
const NoAutofillInput = (props) => <rsComponents.Input {...props} autoComplete="chrome-off" />;

// Mirrors FloatingInput's look exactly (same rounded-xl border-2 box, same
// bg-gray-50/50 fill, same floating uppercase label, same icon slot) so a
// SelectBox dropped next to a FloatingInput in any form lines up instead of
// looking like a different, unstyled component pasted in. react-select
// doesn't support Tailwind's peer-focus/peer-placeholder-shown tricks (its
// "input" isn't a plain sibling text field), so the floating state is driven
// by component state (focused / has a value) instead of CSS pseudo-classes.
const SelectBox = ({
  label,
  name,
  options,
  value,
  onChange,
  error,
  icon: Icon,
  isDisabled,
  isMulti,
  placeholder,
  ...props
}) => {
  const [focused, setFocused] = useState(false);
  const inputId = `select-${name || (label ? label.replace(/\s+/g, "-").toLowerCase() : "field")}`;

  const selectedValue = isMulti
    ? options.filter((opt) => (value || []).includes(opt.value))
    : options.find((opt) => opt.value === value) || null;

  // A resolved option counts as "has a value" even when its own value is ""
  // — many selects here use "" for a real, labeled default choice ("No
  // zone", "All regions", "Use base currency"), not for "nothing picked
  // yet". Floating the label only when the raw value is non-empty would
  // leave that default option's text sitting behind the big placeholder
  // label instead of the box showing it cleanly with a small floated label.
  const hasValue = isMulti ? selectedValue.length > 0 : selectedValue !== null;

  const isFloating = focused || hasValue;

  // Same 56px content-box height as FloatingInput (pt-6 + pb-2 + text-sm
  // line-height, border-box), same rounded-xl/border-2/bg-gray-50/50 as its
  // <input>, same inline-start offset reserved for an icon (2rem vs 0.5rem)
  // — paddingInlineStart rather than paddingLeft so it flips automatically
  // for an RTL language instead of always reserving space on the left.
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: "56px",
      backgroundColor: "rgba(249, 250, 251, 0.5)",
      borderWidth: "2px",
      borderRadius: "0.75rem",
      borderColor: error ? "#f87171" : state.isFocused ? "var(--primary-color)" : "#f3f4f6",
      boxShadow: "none",
      paddingInlineStart: Icon ? "2rem" : "0.5rem",
      paddingTop: "14px",
      paddingBottom: "2px",
      cursor: "pointer",
      transition: "border-color 150ms",
      "&:hover": {
        borderColor: error ? "#f87171" : state.isFocused ? "var(--primary-color)" : "#f3f4f6",
      },
    }),
    valueContainer: (base) => ({ ...base, padding: "0 0.5rem" }),
    input: (base) => ({ ...base, margin: 0, padding: 0, color: "#111827", fontSize: "0.875rem" }),
    singleValue: (base) => ({ ...base, margin: 0, color: "#111827", fontSize: "0.875rem" }),
    multiValue: (base) => ({ ...base, backgroundColor: "#f3f4f6", borderRadius: "0.5rem" }),
    placeholder: (base) => ({ ...base, opacity: 0 }),
    indicatorSeparator: () => ({ display: "none" }),
    indicatorsContainer: (base) => ({ ...base, alignSelf: "center" }),
    dropdownIndicator: (base) => ({ ...base, color: "#9ca3af", padding: "0 8px" }),
    clearIndicator: (base) => ({ ...base, color: "#9ca3af", padding: "0 4px" }),
    menu: (base) => ({
      ...base,
      borderRadius: "0.75rem",
      overflow: "hidden",
      border: "1px solid #f3f4f6",
      boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.08)",
      zIndex: 30,
    }),
    menuList: (base) => ({ ...base, padding: "4px" }),
    option: (base, state) => ({
      ...base,
      borderRadius: "0.5rem",
      fontSize: "0.875rem",
      cursor: "pointer",
      backgroundColor: state.isSelected ? "var(--primary-color)" : state.isFocused ? "#f3f4f6" : "transparent",
      color: state.isSelected ? "#fff" : "#111827",
    }),
  };

  return (
    <div className="relative w-full mb-1">
      <div className="relative flex items-center">
        {Icon && (
          <div className="absolute start-4 z-10 pointer-events-none text-gray-400">
            <Icon className="w-5 h-5" />
          </div>
        )}

        <div className="w-full">
          <Select
            inputId={inputId}
            classNamePrefix="react-select"
            name={name}
            options={options}
            value={selectedValue}
            onChange={(opt) => onChange(isMulti ? (opt || []).map((o) => o.value) : opt?.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onMenuOpen={() => setFocused(true)}
            onMenuClose={() => setFocused(false)}
            isSearchable
            isMulti={isMulti}
            isDisabled={isDisabled}
            placeholder={placeholder ?? ""}
            styles={selectStyles}
            components={{ Input: NoAutofillInput }}
            {...props}
          />
        </div>

        <label
          htmlFor={inputId}
          className={`absolute font-bold uppercase tracking-widest transition-all duration-200
          pointer-events-none
          ${isFloating ? "top-2 text-xs" : "top-4 text-sm font-medium normal-case"}
          ${focused ? "text-[var(--primary-color)]" : "text-gray-400"}
          ${Icon ? "start-12" : "start-4"}`}
        >
          {label}
        </label>
      </div>

      {error && (
        <p className="text-[10px] text-red-500 font-bold mt-1 ms-2 uppercase tracking-tight">
          {error}
        </p>
      )}
    </div>
  );
};

export default SelectBox;
